// DEMS Background Agent Status Bar
// Shows live status of AI agents working in the background
import { useState, useEffect } from 'react';
import { 
  Brain,
  Shield, 
  Users, 
  Lock, 
  CheckCircle,
  AlertTriangle,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface AgentStatus {
  id: string;
  name: string;
  shortName: string;
  status: 'active' | 'idle' | 'alert';
  message?: string;
  icon: React.ReactNode;
}

export function AgentStatusBar() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [agents, setAgents] = useState<AgentStatus[]>([
    {
      id: 'integrity',
      name: 'Evidence Integrity Monitor',
      shortName: 'Integrity',
      status: 'active',
      message: '523 files verified',
      icon: <Lock className="w-3.5 h-3.5" />
    },
    {
      id: 'anomaly',
      name: 'Security Anomaly Detector',
      shortName: 'Security',
      status: 'active',
      message: 'All clear',
      icon: <Shield className="w-3.5 h-3.5" />
    },
    {
      id: 'triage',
      name: 'Submission Triage Agent',
      shortName: 'Triage',
      status: 'active',
      message: '3 pending review',
      icon: <Users className="w-3.5 h-3.5" />
    }
  ]);

  // Simulate periodic status updates
  useEffect(() => {
    const interval = setInterval(() => {
      setAgents(prev => prev.map(agent => {
        if (agent.id === 'integrity') {
          const count = 520 + Math.floor(Math.random() * 10);
          return { ...agent, message: `${count} files verified` };
        }
        if (agent.id === 'triage') {
          const pending = Math.floor(Math.random() * 5);
          return { 
            ...agent, 
            message: pending === 0 ? 'All processed' : `${pending} pending review`,
            status: pending > 3 ? 'alert' : 'active'
          };
        }
        // Occasionally show an alert status
        if (agent.id === 'anomaly' && Math.random() > 0.95) {
          return { ...agent, status: 'alert', message: 'Unusual pattern detected' };
        }
        return agent;
      }));
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  if (isDismissed) return null;

  const hasAlert = agents.some(a => a.status === 'alert');

  return (
    <div className={`fixed bottom-4 right-4 z-40 ${isExpanded ? 'w-80' : 'w-auto'}`}>
      <div className={`bg-white dark:bg-slate-800 rounded-lg shadow-lg border ${hasAlert ? 'border-orange-300 dark:border-orange-600' : 'border-gray-200 dark:border-slate-700'} overflow-hidden`}>
        {/* Compact view */}
        <div 
          className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors ${hasAlert ? 'bg-orange-50 dark:bg-orange-900/20' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className={`p-1.5 rounded-full ${hasAlert ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-purple-100 dark:bg-purple-900/30'}`}>
            <Brain className={`w-4 h-4 ${hasAlert ? 'text-orange-600' : 'text-purple-600'}`} />
          </div>
          
          <div className="flex items-center gap-1.5">
            {agents.map(agent => (
              <div
                key={agent.id}
                className={`w-2 h-2 rounded-full ${
                  agent.status === 'active' ? 'bg-green-500' :
                  agent.status === 'alert' ? 'bg-orange-500 animate-pulse' :
                  'bg-gray-400'
                }`}
                title={`${agent.name}: ${agent.message}`}
              />
            ))}
          </div>
          
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
            {hasAlert ? 'Agent Alert' : `${agents.filter(a => a.status === 'active').length} agents active`}
          </span>
          
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
          ) : (
            <ChevronUp className="w-4 h-4 text-gray-400 ml-auto" />
          )}
          
          <button
            onClick={(e) => { e.stopPropagation(); setIsDismissed(true); }}
            className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Expanded view */}
        {isExpanded && (
          <div className="border-t border-gray-200 dark:border-slate-700">
            <div className="p-3 space-y-2">
              {agents.map(agent => (
                <div 
                  key={agent.id}
                  className={`flex items-center gap-2 p-2 rounded-lg ${
                    agent.status === 'alert' 
                      ? 'bg-orange-50 dark:bg-orange-900/20' 
                      : 'bg-gray-50 dark:bg-slate-700/50'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${
                    agent.status === 'alert'
                      ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600'
                      : 'bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-gray-400'
                  }`}>
                    {agent.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 dark:text-white">{agent.shortName}</p>
                    <p className={`text-xs ${
                      agent.status === 'alert' ? 'text-orange-600' : 'text-gray-500'
                    }`}>
                      {agent.message}
                    </p>
                  </div>
                  {agent.status === 'active' && (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                  {agent.status === 'alert' && (
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                  )}
                </div>
              ))}
            </div>
            
            <div className="px-3 pb-3">
              <Link
                to="/ai-agents"
                className="block w-full text-center py-2 text-xs font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
              >
                View AI Agent Dashboard →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentStatusBar;
